import { Theme, css, useTheme } from '@emotion/react';

import React from 'react';

import { FontSizes, Sizes, Spacing } from 'chatie-design-system';
import { Chatie, Icon } from 'chatie-design-system/icon';

import { DATA_TEST } from '@constants';
import { useGlobalContext } from '@contexts/global';

import { CSSProps } from '../../utils/misc';
import { UsePaginationQueryResult } from '../../utils/query';

const emptyCSS = (theme: Theme) => css`
  padding-top: ${Spacing.xxxlarge}px;
  padding-left: ${Spacing.medium}px;
  padding-right: ${Spacing.medium}px;
  padding-bottom: ${Spacing.medium}px;
  color: ${theme.var.gray400};
  text-align: center;
  line-height: 135%;
  font-size: ${FontSizes.small}px;

  i {
    display: inline-flex;
    margin-bottom: ${Spacing.large}px;
  }
  svg {
    margin-bottom: ${Spacing.large}px;
  }
  i svg {
    margin-bottom: 0;
  }
`;

type Props = {
  empty?: boolean;
  query?: UsePaginationQueryResult<unknown>;
  children?: React.ReactNode;
  hideIcon?: boolean;
};

const Empty = ({
  className,
  empty,
  query,
  hideIcon = false,
  children,
}: Props & CSSProps) => {
  const theme = useTheme();
  const { isCheming } = useGlobalContext();

  const isQueryDataEmpty = query ? query.isFetched && query.count === 0 : false;

  if (isQueryDataEmpty || empty) {
    return (
      <div css={emptyCSS} className={className} data-test={DATA_TEST.EMPTY}>
        {hideIcon ? null : (
          <>
            {!isCheming ? (
              <Icon
                icon={Chatie}
                size={Sizes.xlarge}
                color={theme.var.gray300}
              />
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="33"
                height="39"
                viewBox="0 0 33 39"
                fill="none"
              >
                <path
                  d="M6.28125 29.8817C7.244 28.9849 8.75295 28.1772 11.1333 28.1772C13.1735 28.1772 14.8738 29.0656 16.523 29.9264C18.1084 30.7531 19.5876 31.1527 21.7597 31.1527C24.4566 31.1527 26.2929 30.3939 26.7179 30.0433C26.7179 30.0433 19.2943 36.9037 16.9969 37.1992C14.6995 37.4946 7.28013 31.7924 6.28337 29.8839L6.28125 29.8817Z"
                  fill="#D8D8D8"
                />
                <path
                  d="M13.9451 6.39503H18.9161L21.0478 3.24321C21.7087 2.33783 21.577 1.02015 20.6631 0.374054C20.3294 0.138145 19.932 0 19.5048 0H13.5222C13.0121 0 12.5424 0.197654 12.1705 0.527075C11.4118 1.20505 11.3459 2.37609 11.924 3.21558L13.9451 6.39503Z"
                  fill="#D8D8D8"
                />
                <path
                  d="M30.688 14.1371C29.315 12.1627 26.922 10.7749 24.446 10.5198C23.0582 10.3774 21.7108 10.5942 20.4824 11.1255V9.43166H21.4685C21.9722 9.43166 22.3802 9.0236 22.3802 8.51991C22.3802 8.01621 21.9722 7.60815 21.4685 7.60815H11.5307C11.027 7.60815 10.6189 8.01621 10.6189 8.51991C10.6189 9.0236 11.027 9.43166 11.5307 9.43166H12.5891V11.1553C11.3415 10.6006 9.9686 10.3753 8.55316 10.5198C6.07508 10.7749 3.68202 12.1606 2.30908 14.1371C-0.30501 17.8967 -0.0733534 23.006 2.92117 27.4818C6.29611 32.3977 10.1832 35.6961 15.15 37.8597C15.6006 38.0552 16.0469 38.1594 16.5187 38.1679C16.9523 38.1594 17.4007 38.0552 17.8513 37.8597C22.818 35.6961 26.703 32.3977 30.0865 27.4733C33.0725 23.0081 33.3042 17.8967 30.6901 14.1392L30.688 14.1371ZM14.4126 9.43166H18.661V12.2222C18.4803 12.3667 18.2997 12.5155 18.1275 12.677C17.5261 13.2466 17.0564 13.4782 16.506 13.4782H16.4932C15.9428 13.4782 15.4731 13.2466 14.8716 12.677C14.7228 12.5367 14.5677 12.4113 14.4126 12.2859V9.43166ZM28.5754 26.449C25.4024 31.0715 21.7618 34.1659 17.1223 36.1871C16.8949 36.287 16.6717 36.3401 16.5187 36.3444C16.3274 36.3401 16.1043 36.2848 15.8769 36.1871C11.2353 34.1659 7.59678 31.0694 4.43012 26.4575C1.86915 22.6298 1.63112 18.3069 3.80741 15.1764C4.88705 13.6249 6.77643 12.5346 8.74018 12.3327C8.94846 12.3114 9.17374 12.2987 9.41177 12.2987C10.6317 12.2987 12.1874 12.6472 13.6198 14.0032C14.2107 14.5621 15.15 15.3039 16.4932 15.3039H16.506C17.8491 15.3039 18.7906 14.5621 19.3793 14.0032C21.0902 12.3837 22.9753 12.2009 24.259 12.3348C26.2227 12.5367 28.1142 13.627 29.1918 15.1785C31.368 18.3069 31.1279 22.6298 28.5754 26.449Z"
                  fill="#D8D8D8"
                />
                <path
                  d="M21.2074 21.0448C21.8788 21.0448 22.423 20.4025 22.423 19.6102C22.423 18.8179 21.8788 18.1757 21.2074 18.1757C20.536 18.1757 19.9917 18.8179 19.9917 19.6102C19.9917 20.4025 20.536 21.0448 21.2074 21.0448Z"
                  fill="#D8D8D8"
                />
                <path
                  d="M11.7921 21.0448C12.4635 21.0448 13.0077 20.4025 13.0077 19.6102C13.0077 18.8179 12.4635 18.1757 11.7921 18.1757C11.1207 18.1757 10.5764 18.8179 10.5764 19.6102C10.5764 20.4025 11.1207 21.0448 11.7921 21.0448Z"
                  fill="#D8D8D8"
                />
                <path
                  d="M19.2134 23.2167C17.7024 24.6067 15.2456 24.6088 13.7345 23.2167C13.3647 22.8767 12.7887 22.9 12.4466 23.2699C12.1044 23.6397 12.1299 24.2177 12.4997 24.5578C13.5942 25.5673 15.0352 26.071 16.474 26.071C17.9128 26.071 19.3537 25.5673 20.4482 24.5578C20.818 24.2177 20.8435 23.6397 20.5014 23.2699C20.1592 22.9 19.5832 22.8767 19.2134 23.2167Z"
                  fill="#D8D8D8"
                />
              </svg>
            )}
          </>
        )}
        <div>{children}</div>
      </div>
    );
  }

  return null;
};

export default Empty;
